import { Reference } from '@approvalmax/types';
import { useAtomValue } from 'jotai';
import { domain } from 'modules/data';
import { useCallback } from 'react';
import { ocrIsLoadingState } from 'shared/states';

import { useExchangeRates } from '../../hooks';
import { isFieldReadOnly } from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';

export const useTransactionDate = (
    integrationCode: domain.IntegrationCode | null,
    date: string | null | undefined,
    transactionDate: string | null | undefined
) => {
    if (
        integrationCode &&
        [
            domain.IntegrationCode.NetSuiteBillPayment,
            domain.IntegrationCode.NetSuiteInvoice,
            domain.IntegrationCode.NetSuiteJournalEntry,
        ].includes(integrationCode)
    ) {
        return transactionDate;
    }

    return date;
};

export const useIsCurrencyFieldDisabled = (
    currencies: domain.NetSuiteCurrency[],
    currencyFieldState: domain.DocumentFieldState,
    request: domain.Request,
    transactionNumber?: string | null
) => {
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);

    return (
        ocrIsLoading ||
        !currencies.length ||
        Boolean(transactionNumber) ||
        isFieldReadOnly(currencyFieldState) ||
        (request.integrationCode !== domain.IntegrationCode.NetSuiteJournalEntry &&
            request.origin === domain.RequestOrigin.NetSuite &&
            request.statusV2 === domain.RequestStatusV2.OnReview)
    );
};

export const useIsExchangeRateFieldDisabled = (
    request: domain.Request,
    exchangeRateFieldState: domain.DocumentFieldState
) =>
    isFieldReadOnly(exchangeRateFieldState) ||
    (request.origin === domain.RequestOrigin.NetSuite &&
        request.integrationCode === domain.IntegrationCode.NetSuiteExpenseReport);

export const useCurrencySectionExchangeRates = (
    companyId: string,
    transactionDate: string | null | undefined,
    currency: Reference | null | undefined
) => {
    const {
        data: exchangeRates,
        isFetching: isFetchingExchangeRates,
        isError: isErrorExchangeRates,
        refetch: refetchExchangeRates,
    } = useExchangeRates(companyId, transactionDate);

    const { refetch: fetchExchangeRatesToRequestCurrency, isError: isErrorExchangeRatesToRequestCurrency } =
        useExchangeRates(companyId, transactionDate, currency?.id, true);

    const refetchRates = useCallback(() => {
        if (isErrorExchangeRates) {
            refetchExchangeRates();
        }

        if (isErrorExchangeRatesToRequestCurrency) {
            fetchExchangeRatesToRequestCurrency();
        }
    }, [
        isErrorExchangeRates,
        isErrorExchangeRatesToRequestCurrency,
        refetchExchangeRates,
        fetchExchangeRatesToRequestCurrency,
    ]);

    return {
        exchangeRates,
        isFetchingExchangeRates,
        isErrorExchangeRates: isErrorExchangeRates || isErrorExchangeRatesToRequestCurrency,
        refetchExchangeRates: refetchRates,
    };
};
